<template>
  <teleport to="body">
    <div :class="fadeTransition" class="modal">
      <div class="modal-body" :class="bounceTransition">
        <div class="modal-header">
          <h1>Select Your Material</h1>
          <div class="close-svg" v-on:click="closeModal">
            <CloseSVG :propId="'materials-modal-close'" />
          </div>
        </div>
        <div id="modal-materials-container">
          <div
            class="modal-materials"
            v-for="material in materials"
            :key="material.id"
            :id="material.id"
          >
            <input
              v-if="Number(material.id) === currentLabelMaterialId"
              checked
              name="labelMaterialInput"
              type="radio"
              :id="material.image_id"
              @input="handleInput(material)"
            />
            <input
              v-else
              name="labelMaterialInput"
              type="radio"
              :id="material.image_id"
              @input="handleInput(material)"
            />
            <label :for="material.image_id" class="material-option">
              <img
                v-if="material.image_id"
                :src="material.image_url"
                :alt="material.title"
              />
              <div class="material-info">
                <p>{{ material.title }}</p>
                <div v-html="material.popup_description"></div>
              </div>
            </label>
          </div>
        </div>

        <button
          aria-label="Open change label material modal"
          class="modal-select-btn"
          @click="changeMaterial()"
        >
          Change Material
        </button>
      </div>
    </div>
  </teleport>
</template>

<script lang="ts">
import { defineComponent, ref, Ref, inject, computed, watch, onMounted } from "vue";
import { store } from "@/store";
import CloseSVG from "@/components/CloseSVG.vue";
import { ActionTypes } from "@/store/actions";

export default defineComponent({
  components: {
    CloseSVG,
  },
  setup() {
    const closeModal = inject("closeModal") as () => void;
    const bounceTransition = inject("bounceTransition") as Ref<string>;
    const fadeTransition = inject("fadeTransition") as Ref<string>;

    const materials = computed<any>(() =>
      store.getters.materials.sort((a: any, b: any) => {
        if (Number(a.position) > Number(b.position)) {
          return 1;
        } else {
          return -1;
        }
      })
    );

    const currentLabelType = computed<string>(() => store.state.currentLabelType);
    const currentLabelMaterialId = computed<number>(() => store.state.currentLabelMaterialId);

    const currentMaterial = ref({} as any);
    const selectedMaterialId = ref(currentLabelMaterialId.value);
    watch(currentLabelMaterialId, (newId: number) => {
      selectedMaterialId.value = newId;

      const foundMaterial = materials.value.find((material: any) => Number(material.id) === newId);

      if (foundMaterial) {
        currentMaterial.value = foundMaterial;
      } else {
        if (
          currentLabelType.value === "rollprinted" ||
          currentLabelType.value === "custom-rollprinted"
        ) {
          currentMaterial.value.title = "White BOPP Permanent";
        } else {
          currentMaterial.value.title = "Standard White";
        }
      }
    });

    onMounted(() => {
      const foundMaterial = materials.value.find(
        (material: any) => Number(material.id) === currentLabelMaterialId.value
      );

      if (foundMaterial) {
        currentMaterial.value = foundMaterial;
      } else {
        if (
          currentLabelType.value === "rollprinted" ||
          currentLabelType.value === "custom-rollprinted"
        ) {
          currentMaterial.value.title = "White BOPP Permanent";
        } else {
          currentMaterial.value.title = "Standard White";
        }
      }
    });

    onMounted(() => {
      const foundMaterial = materials.value.find(
        (material: any) => Number(material.id) === currentLabelMaterialId.value
      );

      if (foundMaterial && foundMaterial.id) {
        const materialsContainer = document.getElementById(foundMaterial.id.toString());

        if (materialsContainer) {
          materialsContainer.style.background = "#0096ff15";

          const listContainer = document.getElementById("modal-materials-container");

          if (listContainer) {
            const sizeContainerBoundingRect = materialsContainer.getBoundingClientRect();
            const listContainerBoundingRect = listContainer.getBoundingClientRect();

            // prettier-ignore
            listContainer.scroll( 0, sizeContainerBoundingRect.top - listContainerBoundingRect.top - sizeContainerBoundingRect.height / 2 );
          }
        }
      }
    });

    const selectedMaterial = ref({} as any);
    const handleInput = (mat: any): void => {
      const foundMaterial = materials.value.find((material: any) => material.id === mat.id);

      if (foundMaterial) {
        const materialContainer = document.getElementById(foundMaterial.id);
        const prevMaterialContainer = document.getElementById(selectedMaterialId.value.toString());

        if (materialContainer && prevMaterialContainer) {
          prevMaterialContainer.style.background = "";
          materialContainer.style.background = "#0096ff15";
          selectedMaterialId.value = Number(foundMaterial.id);
        }

        selectedMaterial.value = foundMaterial;
      }
    };

    const changeMaterial = async (): Promise<void> => {
      await store.dispatch(ActionTypes.CHANGE_LABEL_MATERIAL, selectedMaterial.value);
      closeModal();
    };

    return {
      closeModal,
      bounceTransition,
      fadeTransition,
      materials,
      currentLabelMaterialId,
      currentMaterial,
      handleInput,
      changeMaterial,
    };
  },
});
</script>

<style scoped>
.modal-body {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  background-color: white;
  width: 500px;
  height: 600px;
  padding: 25px;
  border-radius: 6px;
  overflow: auto;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 5%;
}

.modal-header h1 {
  font-weight: 600;
  font-size: 22px;
  letter-spacing: -0.25px;
}

.close {
  border-radius: 50%;
  border: solid 1px black;
  background-color: white;
  height: 25px;
  width: 25px;
}

#modal-materials-container {
  overflow-y: scroll;
  width: 100%;
  border: 1px solid rgb(164, 160, 160);
  height: 450px;
}

.modal-materials {
  border: 1px solid rgb(229, 229, 229);
  padding: 10px;
  margin: 10px;
  display: flex;
  align-items: center;
}

.material-option {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 12px;
  padding: 10px;
}

.material-option img {
  width: 150px;
  height: 150px;
}

.material-info {
  width: 50%;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.material-info p {
  font-size: 16px;
  font-weight: 700;
  margin-bottom: 10px;
}

.modal-select-btn {
  border: none;
  border-radius: 2px;
  background: #ff6c00;
  color: white;
  font-size: 16px;
  height: 40px;
  padding: 0 2px 0 2px;
  text-decoration: none;
  text-align: center;
  width: 100%;
  text-shadow: none;
}
</style>
